import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CobroCuotasCreditosModel,
  ConstitucionDelCreditoCreditosModel,
  CreditosModel,
  DeudaACortoPlazoCreditosModel
} from './creditos.model';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';
import { unwrapV2, V2ApiResponse } from '../../../modules_v2/shared/utils/v2-api-response.util';

const CREDITOS_ENDPOINT = '/creditos';
const CONSTITUCIONCREDITO_ENDPOINT = '/constituciondelcreditocreditos';
const DEUDACORTOPLAZO_ENDPOINT = '/deudasacortoplazocreditos';
const COBROCUOTAS_ENDPOINT = '/cobrocuotascreditos';
const V2_ENDPOINT = '/api/v2/credits';

@Injectable({
  providedIn: 'root',
})
export class CreditosService {
  constructor(private http: HttpClient) {}

  public downloadFileAttachment(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('creditosId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/creditos/file/downloadexcel', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'application/vnd.ms-excel' });
    });
  }

  public get(uid: string): Promise<CreditosModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<CreditosModel>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(uid))),
      'No se pudo cargar el crédito.'
    );
  }

  public list(offset?: number, limit?: number): Promise<ResultModel<CreditosModel>> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<CreditosModel[]>>(environment.apiUrl + V2_ENDPOINT)),
      'No se pudieron cargar los créditos.'
    ).then((list) => {
      const result = new ResultModel<CreditosModel>();
      result.list = Array.isArray(list) ? list : [];
      result.totalItems = result.list.length;
      return result;
    });
  }

  public add(creditos: CreditosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + CREDITOS_ENDPOINT, creditos)
    ).then((response) => response.data);
  }

  public edit(creditos: CreditosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + CREDITOS_ENDPOINT, creditos)
    ).then((response) => response.data);
  }

  public editFileAttachment(creditos: CreditosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + CREDITOS_ENDPOINT + '/file', creditos)
    ).then((response) => response.data);
  }

  public delete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('creditosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + CREDITOS_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public constitucionDelCreditoList(
    creditosId: string
  ): Promise<ConstitucionDelCreditoCreditosModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<ConstitucionDelCreditoCreditosModel[]>>(
        environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(creditosId) + '/constitutions')),
      'No se pudieron cargar las constituciones.'
    ).then((list) => list || []);
  }

  public constitucionDelCreditoCreate(
    constituciondelcredito: ConstitucionDelCreditoCreditosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCIONCREDITO_ENDPOINT, constituciondelcredito)
    ).then((response) => response.data);
  }

  public constitucionDelCreditoUpdate(
    constituciondelcredito: ConstitucionDelCreditoCreditosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCIONCREDITO_ENDPOINT, constituciondelcredito)
    ).then((response) => response.data);
  }

  public constitucionDelCreditoDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('constituciondelcreditocreditosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCIONCREDITO_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public deudaACortoPlazoList(
    deudaacortoplazoUid: string
  ): Promise<DeudaACortoPlazoCreditosModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<DeudaACortoPlazoCreditosModel[]>>(
        environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(deudaacortoplazoUid) + '/short-term-debts')),
      'No se pudieron cargar las deudas a corto plazo.'
    ).then((list) => list || []);
  }

  public deudaACortoPlazoCreate(
    deudaACortoPlazo: DeudaACortoPlazoCreditosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoUpdate(
    deudaACortoPlazo: DeudaACortoPlazoCreditosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('deudasacortoplazocreditosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public cobroCoutasList(cobrocuotasUid: string): Promise<CobroCuotasCreditosModel[]> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<CobroCuotasCreditosModel[]>>(
        environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(cobrocuotasUid) + '/installments')),
      'No se pudieron cargar las cuotas cobradas.'
    ).then((list) => list || []);
  }

  public cobroCoutasCreate(cobroDeCuotas: CobroCuotasCreditosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + COBROCUOTAS_ENDPOINT, cobroDeCuotas)
    ).then((response) => response.data);
  }

  public cobroCoutasUpdate(cobroDeCuotas: CobroCuotasCreditosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + COBROCUOTAS_ENDPOINT, cobroDeCuotas)
    ).then((response) => response.data);
  }

  public cobroCoutasDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('cobrocuotascreditosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + COBROCUOTAS_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }
}